import { createSlice, createAction, PayloadAction, createSelector } from '@reduxjs/toolkit'
import { ApiError, PageMetadata, RequestStatus, SortDirection } from 'features/common'
import {
  DemoMap,
  Map,
  MapDataset,
  MapsQueryFilters,
  MapFieldToOrderBy,
  MapsFilter,
  MapsOrder,
  MapType,
  CreateDemoMapRequest,
  MapSettings,
  CartoKeplerMapConfig
} from '../types'
import { duplicateInitialState, duplicateMapsReducers, DuplicateMapState } from 'features/maps/state/duplicateMapSlice'
import { MAPS_REMOTE_PAGE_SIZE } from '../api/maps'
import { RootState } from 'app/state/store'
import { createReduxActionDefinition } from 'app/state/action'
import { Agent, AgentConfig } from '../agent-types'

export const defaultMapSettings: MapSettings = {
  controlCacheTime: false,
  cacheTime: 0,
  exportViewportData: false,
  exportPDF: false,
  comments: false,
  addressSearchBar: false,
  scrollWheelZoom: true,
  featureSelectionTool: false,
  reorderLayers: false,
  showPerformanceWarnings: false,
  showMyLocationButton: false,
  showTitlePublicMap: false,
  showMeasureDistanceTool: false,
  basemapsSelector: false,
  measurementUnit: 'kilometers'
}

export interface MapsState extends DuplicateMapState {
  myMaps: Array<Map>
  myMapsMetadata?: PageMetadata
  myMapsStatus: RequestStatus
  myMapsError?: ApiError
  currentMap?: Map
  currentMapDatasets: MapDataset[]
  currentMapStatus: RequestStatus
  currentMapInitialConfig?: CartoKeplerMapConfig
  lastSavedMapConfig?: CartoKeplerMapConfig
  myMapsFilters: MapsQueryFilters
  mapEditStatus: RequestStatus
  mapEditError?: ApiError
  fetchMapError?: ApiError
  refresh?: boolean
  changeMapPrivacyStatus: RequestStatus
  changeMapPrivacyError?: ApiError
  latestMaps: Array<Map>
  latestMapsStatus: RequestStatus
  latestMapsError?: ApiError
  demoMaps: Array<DemoMap>
  demoMapsStatus: RequestStatus
  demoMapsError?: ApiError
  createDemoMapError?: ApiError
  creatingDemoMapsIds: Array<string>
  mapSettings: MapSettings
}

const initialState: MapsState = {
  myMaps: [],
  myMapsStatus: RequestStatus.Idle,
  myMapsFilters: {
    search: '',
    order: {
      by: MapFieldToOrderBy.UpdatedAt,
      direction: SortDirection.Desc
    },
    filter: undefined,
    pagination: {
      size: MAPS_REMOTE_PAGE_SIZE,
      page: 1
    }
  },
  mapEditStatus: RequestStatus.Idle,
  refresh: false,
  changeMapPrivacyStatus: RequestStatus.Idle,
  currentMapDatasets: [],
  latestMaps: [],
  currentMapStatus: RequestStatus.Idle,
  latestMapsStatus: RequestStatus.Idle,
  ...duplicateInitialState,
  demoMaps: [],
  demoMapsStatus: RequestStatus.Idle,
  creatingDemoMapsIds: [],
  mapSettings: defaultMapSettings
}

const name = 'maps'
const MAPS_FETCH_REQUEST = `${name}/fetch_maps_request`
const LATEST_MAPS_FETCH_REQUEST = `${name}/fetch_latest_maps_request`
const EDIT_MAP_TITLE_DESCRIPTION = `${name}/edit_map_title_description`
const DEMO_MAPS_FETCH_REQUEST = `${name}/fetch_demo_maps_request`
const DEMO_MAP_CREATE_REQUEST = `${name}/create_demo_map_request`
const RENAME_DATASET_REQUEST = `${name}/rename_dataset_request`

const asyncActions = {
  fetchMapsRequest: createAction<MapsQueryFilters | undefined>(MAPS_FETCH_REQUEST),
  createDemoMapRequest: createAction<CreateDemoMapRequest>(DEMO_MAP_CREATE_REQUEST),
  fetchLatestMapsRequest: createAction<undefined>(LATEST_MAPS_FETCH_REQUEST),
  editMapTitleDescription: createAction<Pick<Map, 'id' | 'title' | 'description'>>(EDIT_MAP_TITLE_DESCRIPTION),
  fetchDemoMapsRequest: createAction(DEMO_MAPS_FETCH_REQUEST),
  renameDatasetRequest: createAction<{ mapId: string; datasetId: string; label: string }>(RENAME_DATASET_REQUEST)
}

const reducers = {
  setMyMaps: (state: MapsState, action: PayloadAction<Array<Map>>) => {
    state.myMaps = action.payload
  },
  setMapFilterPagination: (state: MapsState, action: PayloadAction<{ size?: number; page: number }>) => {
    state.myMapsFilters.pagination = action.payload
  },
  setMapFilterSearch: (state: MapsState, action: PayloadAction<string | undefined>) => {
    state.myMapsFilters.search = action.payload
  },
  setMapFilterOrder: (state: MapsState, action: PayloadAction<MapsOrder | undefined>) => {
    state.myMapsFilters.order = action.payload
  },
  setMapFilter: (state: MapsState, action: PayloadAction<MapsFilter | undefined>) => {
    state.myMapsFilters.filter = action.payload
  },
  setMyMapsMetadata: (state: MapsState, action: PayloadAction<PageMetadata>) => {
    state.myMapsMetadata = action.payload
  },
  setMyMapsStatus: (state: MapsState, action: PayloadAction<RequestStatus>) => {
    state.myMapsStatus = action.payload
  },
  setMyMapsError: (state: MapsState, action: PayloadAction<ApiError>) => {
    state.myMapsError = action.payload
  },
  setCurrentMap: (state: MapsState, action: PayloadAction<Map | undefined>) => {
    if (!state.currentMap || !action.payload) {
      state.currentMap = action.payload
    }
  },
  updateCurrentMap: (state: MapsState, action: PayloadAction<Partial<Map> | undefined>) => {
    const newCurrentMap = action.payload
    if (state.currentMap && (!newCurrentMap?.id || state.currentMap?.id === newCurrentMap?.id)) {
      state.currentMap = { ...state.currentMap, ...action.payload }
    }
  },
  setLastSavedMapConfig: (state: MapsState, action: PayloadAction<CartoKeplerMapConfig | undefined>) => {
    state.lastSavedMapConfig = action.payload
  },
  setCurrentMapDatasets: (state: MapsState, action: PayloadAction<Array<MapDataset>>) => {
    state.currentMapDatasets = action.payload
  },
  setCurrentMapAndDatasets: (state: MapsState, action: PayloadAction<{ map: Map; datasets: MapDataset[] }>) => {
    const { map, datasets } = action.payload
    state.currentMap = map
    state.currentMapDatasets = datasets
  },
  addCurrentMapDataset: (state: MapsState, action: PayloadAction<MapDataset>) => {
    const { payload: dataset } = action
    const datasets = state.currentMapDatasets

    // if id is provided, we need to check if it already exists
    if (dataset.id) {
      const exist = datasets.some((d: MapDataset) => {
        d.id === dataset.id
      })

      if (exist) {
        throw new Error(`Dataset already exist, cannot be added`)
      }
    }
    state.currentMapDatasets = [...state.currentMapDatasets, action.payload]
  },
  updateCurrentMapDataset: (state: MapsState, action: PayloadAction<{ id: string; dataset: Partial<MapDataset> }>) => {
    const { id, dataset } = action.payload
    const datasets = state.currentMapDatasets
    if (!id) {
      throw new Error('Not id provided at updateCurrentMapDataset')
    }

    const datasetIdx = datasets.findIndex((d: MapDataset) => d.id === id)
    if (datasetIdx === -1) {
      throw new Error(`No dataset found for id: ${id}`)
    }
    state.currentMapDatasets[datasetIdx] = { ...datasets[datasetIdx], ...dataset }
  },
  deleteCurrentMapDataset: (state: MapsState, action: PayloadAction<string>) => {
    const id = action.payload
    const datasets = state.currentMapDatasets

    const datasetIdx = datasets.findIndex((d: MapDataset) => d.id === id)
    if (datasetIdx == -1) {
      throw new Error(`No dataset found for id: ${id}`)
    }
    datasets.splice(datasetIdx, 1)
  },
  setAgentEnabledForViewer: (state: MapsState, action: PayloadAction<boolean>) => {
    if (state.currentMap?.agent) {
      state.currentMap.agent.enabledForViewer = action.payload
    }
  },
  setAgentConfig: (state: MapsState, action: PayloadAction<AgentConfig>) => {
    if (state.currentMap?.agent) {
      state.currentMap.agent.config = action.payload
    }
  },
  deleteAgent: (state: MapsState) => {
    if (state.currentMap?.agent) {
      state.currentMap.agent.enabledForViewer = false
      state.currentMap.agent.config = undefined
      state.currentMap.agent.issues = []
      state.currentMap.agent.token = undefined
    }
  },
  setCurrentMapTitle: (state: MapsState, action: PayloadAction<string>) => {
    if (state.currentMap) {
      state.currentMap.title = action.payload
    }
  },
  setCurrentMapStatus: (state: MapsState, action: PayloadAction<RequestStatus>) => {
    state.currentMapStatus = action.payload
  },
  setCurrentMapInitialConfig: (state: MapsState, action: PayloadAction<CartoKeplerMapConfig | undefined>) => {
    if (!state.currentMapInitialConfig || !action.payload) {
      state.currentMapInitialConfig = action.payload
    }
  },
  setEditMapStatus: (state: MapsState, action: PayloadAction<RequestStatus>) => {
    state.mapEditStatus = action.payload
  },
  setEditMapError: (state: MapsState, action: PayloadAction<ApiError | undefined>) => {
    state.mapEditError = action.payload
  },
  setFetchMapError: (state: MapsState, action: PayloadAction<ApiError | undefined>) => {
    state.fetchMapError = action.payload
  },
  setChangeMapPrivacyStatus: (state: MapsState, action: PayloadAction<RequestStatus>) => {
    state.changeMapPrivacyStatus = action.payload
  },
  setChangeMapPrivacyError: (state: MapsState, action: PayloadAction<ApiError>) => {
    state.changeMapPrivacyError = action.payload
  },
  setLatestMaps: (state: MapsState, action: PayloadAction<Array<Map>>) => {
    state.latestMaps = action.payload
  },
  setLatestMapsStatus: (state: MapsState, action: PayloadAction<RequestStatus>) => {
    state.latestMapsStatus = action.payload
  },
  setLatestMapsError: (state: MapsState, action: PayloadAction<ApiError>) => {
    state.latestMapsError = action.payload
  },
  setDemoMaps: (state: MapsState, action: PayloadAction<Array<DemoMap>>) => {
    state.demoMaps = action.payload
  },
  setDemoMapsStatus: (state: MapsState, action: PayloadAction<RequestStatus>) => {
    state.demoMapsStatus = action.payload
  },
  setDemoMapsError: (state: MapsState, action: PayloadAction<ApiError>) => {
    state.demoMapsError = action.payload
  },
  setCreateDemoMapError: (state: MapsState, action: PayloadAction<ApiError | undefined>) => {
    state.createDemoMapError = action.payload
  },
  addCreatingDemoMapsId: (state: MapsState, action: PayloadAction<string>) => {
    state.creatingDemoMapsIds.push(action.payload)
  },
  removeCreatingDemoMapsId: (state: MapsState, action: PayloadAction<string>) => {
    state.creatingDemoMapsIds = state.creatingDemoMapsIds.filter((id) => id !== action.payload)
  },
  setMapSettings: (state: MapsState, action: PayloadAction<MapSettings>) => {
    state.mapSettings = action.payload
  },
  updateMapSettings: (state: MapsState, action: PayloadAction<Partial<MapSettings>>) => {
    state.mapSettings = { ...state.mapSettings, ...action.payload }
  },
  ...duplicateMapsReducers
}

export const openDemoMapActionDefinition = createReduxActionDefinition(
  (dispatch, demoMap: CreateDemoMapRequest) => dispatch(createDemoMapRequest(demoMap)),
  {
    status: (state, payload) =>
      state.maps.createDemoMapError
        ? RequestStatus.Rejected
        : state.maps.creatingDemoMapsIds.includes(payload.id)
        ? RequestStatus.Pending
        : RequestStatus.Idle,
    error: (state) => state.maps.createDemoMapError,
    dismissResult: (dispatch) => {
      dispatch(setCreateDemoMapError(undefined))
    }
  }
)

export const MapsSlice = createSlice({
  name,
  initialState,
  reducers
})

export const {
  setMyMaps,
  setMyMapsStatus,
  setMyMapsMetadata,
  setMyMapsError,
  setCurrentMap,
  setCurrentMapDatasets,
  setCurrentMapAndDatasets,
  setCurrentMapStatus,
  setCurrentMapInitialConfig,
  setLastSavedMapConfig,
  addCurrentMapDataset,
  updateCurrentMapDataset,
  deleteCurrentMapDataset,
  updateCurrentMap,
  setMapFilterPagination,
  setMapFilterSearch,
  setMapFilterOrder,
  setMapFilter,
  setCurrentMapTitle,
  setEditMapStatus,
  setEditMapError,
  setFetchMapError,
  setChangeMapPrivacyStatus,
  setChangeMapPrivacyError,
  setLatestMaps,
  setLatestMapsStatus,
  setLatestMapsError,
  setDuplicatingMapId,
  setDryRunDuplicateMapStatus,
  setDuplicateMapStatus,
  setDuplicateMapError,
  setDuplicateMapType,
  setUnavailableConnections,
  setDemoMaps,
  setDemoMapsStatus,
  setDemoMapsError,
  setCreateDemoMapError,
  addCreatingDemoMapsId,
  removeCreatingDemoMapsId,
  setMapSettings,
  updateMapSettings,
  setAgentEnabledForViewer,
  setAgentConfig,
  deleteAgent
} = MapsSlice.actions

export const {
  fetchMapsRequest,
  editMapTitleDescription,
  fetchLatestMapsRequest,
  fetchDemoMapsRequest,
  createDemoMapRequest,
  renameDatasetRequest
} = asyncActions

export const getCurrentMap = (state: RootState) => state.maps.currentMap

export const getAgent = (state: RootState): Agent | undefined => state.maps.currentMap?.agent

export const getDatasetById = createSelector(
  (state: RootState) => state.maps.currentMapDatasets,
  (_: unknown, datasetId: string) => datasetId,
  (datasets, datasetId) => datasets.find((d) => d.id === datasetId)
)

export const getDatasetsByType = createSelector(
  (state: RootState) => state.maps.currentMapDatasets,
  (_: unknown, type: MapType) => type,
  (datasets, type) => datasets.filter((d) => d.type === type)
)

export const getDemoMapsSorted = createSelector(
  (state: RootState) => state.maps.demoMaps,
  (state: RootState) => state.account.data.businessVerticals,
  (demoMaps, accountVerticals) =>
    [...demoMaps]
      .sort((demo1, demo2) => {
        if (!demo1.businessVerticals || !demo2.businessVerticals) return 0

        const commonVerticalsDemo1 = demo1.businessVerticals.filter((v) => accountVerticals?.includes(v)).length
        const commonVerticalsDemo2 = demo2.businessVerticals.filter((v) => accountVerticals?.includes(v)).length

        return commonVerticalsDemo2 - commonVerticalsDemo1
      })
      .filter(Boolean)
      .slice(0, 3)
)

export default MapsSlice.reducer
